/**
 * ffmpeg subprocess pool.
 *
 * A small set of long-lived ffmpeg *runner* processes ({@link ./runner-process.ts})
 * that each spawn one `ffmpeg`/`ffprobe` per request and stay warm between calls.
 * The supervisor mechanics (lazy spawn, readiness handshake, idle reuse, waiter
 * queue, drain, crash → respawn) come from the generic
 * {@link ../worker-pool/worker-pool.js WorkerPool}; this module is its second
 * {@link WorkerDriver} (after cpu-offload).
 *
 * ffmpeg's wall-clock + RSS ceilings are enforced by the runner *on its own
 * ffmpeg child* (not by the pool's RSS monitor), because the heavy process is the
 * runner's child, not the runner itself: a breach SIGKILLs the ffmpeg process and
 * comes back as `outcome: 'timeout' | 'oom'` while the runner stays warm. The
 * pool's own per-call timeout is set generously above the runner's, purely as a
 * backstop for a wedged *runner*.
 *
 * Bound caveat: this guarantee holds only *while the runner lives*. If the pool
 * SIGKILLs the runner itself (a crash, the backstop timeout, or drain), its
 * in-flight ffmpeg child is reparented and runs uncapped until it finishes or the
 * container is torn down. That window is rare (a wedged/crashed runner) and on
 * Fargate is reaped at task stop; tightening it (process-group kill) is deferred
 * to the Phase-5 breach-policy work on the generic pool.
 *
 * `FfmpegPool` is a thin facade exposing the ffmpeg-shaped {@link FfmpegRunSpec}
 * `run` API every call site speaks.
 *
 * @module ffmpeg-pool/pool
 */

import { spawn } from 'node:child_process';
import { existsSync } from 'node:fs';
import { createRequire } from 'node:module';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import type { Writable } from 'node:stream';
import type { BrowserLogger } from '../logger.js';
import {
  type ChildHandle,
  type DriverHooks,
  type WorkerDriver,
  WorkerPool,
  WorkerPoolError,
  type WorkerPoolLeaseGraceCounts,
  type WorkerPoolResourceStats,
  type WorkerPoolSaturationStats,
  type WorkerPoolRetirementCounts,
  WorkerPoolTimeoutError,
} from '../worker-pool/worker-pool.js';
import {
  encodeFrame,
  FrameDecoder,
  READY_ID,
  type FfmpegRequest,
  type FfmpegResponse,
  type FfmpegRunResult,
  type FfmpegRunSpec,
} from './protocol.js';

/** The runner crashed, wedged (pool backstop timeout), or could not be spawned. */
export class FfmpegWorkerError extends Error {
  readonly _tag = 'FfmpegWorkerError';
  constructor(message: string) {
    super(message);
    this.name = 'FfmpegWorkerError';
  }
}

/** Snapshot of pool occupancy + the kill/outcome counters, for host telemetry. */
export interface FfmpegPoolStats {
  /** Driver label (always `'ffmpeg'`) — the per-driver telemetry dimension. */
  driver: string;
  total: number;
  idle: number;
  busy: number;
  waiting: number;
  /**
   * Lifetime kill counts by cause. `timeout`/`oom` are ffmpeg-child breaches the
   * runner reported (the runner stayed warm); `crash` counts every runner the pool
   * itself SIGKILLed — a genuine crash *or* the pool's backstop timeout firing on a
   * wedged runner (the pool's RSS monitor is off for this driver, so its
   * timeout/oom counters can only be runner faults and fold in here).
   */
  kills: { timeout: number; oom: number; crash: number };
  /** Lifetime hard runner retirements with precise bounded reasons. */
  retirements: WorkerPoolRetirementCounts;
  /** Lifetime delayed-sweep lease grace outcomes. */
  leaseGrace: WorkerPoolLeaseGraceCounts;
  /** Highest ffmpeg-child RSS the runners have reported (bytes; 0 if none). */
  rssHighWaterBytes: number;
  /** Queue wait / depth high-water / time starved — see {@link WorkerPoolSaturationStats}. */
  saturation: WorkerPoolSaturationStats;
  /**
   * Per-driver CPU percentiles from the calibration sampler; present only while
   * sampling is on. vCPU folds in the reaped ffmpeg child (see the sampler); RSS
   * for this driver is served by {@link rssHighWaterBytes}, so the sampler reports
   * no `rssBytes` here.
   */
  resourceStats?: WorkerPoolResourceStats;
}

export interface FfmpegPoolOptions {
  /** Max concurrent runner processes. Runners spawn lazily up to this. */
  size: number;
  /** Default per-call wall-clock budget for the ffmpeg child (runner-enforced). */
  defaultTimeoutMs: number;
  /** Default per-child RSS cap (bytes). Unset/0 ⇒ the runner's RSS sampler is off. */
  rssBytes?: number;
  /** Override the runner entry path (tests). Defaults to the resolved sibling. */
  runnerPath?: string;
  /** Override the runtime used to spawn runners (defaults to the current bun/node). */
  execPath?: string;
  /** Injectable spawn (tests). Defaults to node:child_process spawn. */
  spawn?: typeof spawn;
  /** Arm the per-driver CPU calibration sampler at this cadence (ms). Unset ⇒ off. */
  measureResourcesMs?: number;
  logger?: BrowserLogger;
}

/**
 * Margin (ms) added on top of the per-call timeout for the pool's own backstop
 * deadline, so the runner's in-thread timeout (which reliably kills the ffmpeg
 * child) always fires first and the pool backstop only trips for a wedged runner.
 */
const POOL_BACKSTOP_MARGIN_MS = 30_000;

/**
 * Resolve the runner entry, tolerating three layouts (mirrors cpu-offload):
 * 1. source / `bun test` — the sibling `.ts` next to this module;
 * 2. browser-core's own dist — `dist/ffmpeg-pool/runner-process.js`;
 * 3. a bundled host (apps/api `bun build` inlines browser-core into
 *    `dist/server.js`, so `import.meta.url` points at the host bundle) — resolve
 *    via the package itself.
 */
export function resolveFfmpegRunnerPath(): string {
  const candidates = [
    new URL('./runner-process.ts', import.meta.url),
    new URL('./runner-process.js', import.meta.url),
    new URL('./ffmpeg-pool/runner-process.js', import.meta.url),
  ].map((u) => fileURLToPath(u));
  const found = candidates.find((p) => existsSync(p));
  if (found) return found;

  try {
    const pkgJson = createRequire(import.meta.url).resolve('@chatsdet/browser-core/package.json');
    const viaPkg = join(dirname(pkgJson), 'dist', 'ffmpeg-pool', 'runner-process.js');
    if (existsSync(viaPkg)) return viaPkg;
    candidates.push(viaPkg);
  } catch {
    // fall through to the throw
  }
  throw new FfmpegWorkerError(`runner entry not found; looked in: ${candidates.join(', ')}`);
}

/**
 * The runner entry to spawn, failing loudly at CONSTRUCTION if it isn't there.
 *
 * {@link resolveFfmpegRunnerPath} only ever returns a path it found, so this check
 * bites on a caller-supplied override. Validating here matters because a missing
 * entry can never succeed: without it every call spawns, dies "before ready", and
 * reports a per-call `runner_fault` forever — N identical mysteries instead of one
 * accurate "the pool cannot be built" (which is the reason carrying its own
 * single-event alarm; see `worker-pool/inline-fallback.ts`).
 */
function resolveRunnerEntry(override?: string): string {
  if (override === undefined) return resolveFfmpegRunnerPath();
  if (!existsSync(override)) {
    throw new FfmpegWorkerError(`runner entry does not exist: ${override}`);
  }
  return override;
}

/**
 * A framed-stdio connection to one ready runner: dispatches one request at a
 * time (the pool guarantees one in-flight unit per child) and correlates the
 * matching response by id. A stdin-write failure or a decoder error is fatal —
 * it retires the runner via {@link DriverHooks.onFatal}.
 */
class FfmpegConn {
  private current: {
    id: number;
    resolve: (r: FfmpegRunResult) => void;
    reject: (e: Error) => void;
  } | null = null;
  private nextId = 1;

  constructor(
    private readonly stdin: Writable | null,
    private readonly onFatal: (err: Error) => void
  ) {}

  /** Send one resolved request and resolve with the runner's result frame. */
  run(req: Omit<FfmpegRequest, 'id'>): Promise<FfmpegRunResult> {
    return new Promise<FfmpegRunResult>((resolve, reject) => {
      const id = this.nextId++;
      this.current = { id, resolve, reject };
      if (!this.stdin) {
        this.fatal(new FfmpegWorkerError('runner has no stdin'), reject);
        return;
      }
      try {
        this.stdin.write(encodeFrame({ ...req, id } satisfies FfmpegRequest));
      } catch (err) {
        this.fatal(
          new FfmpegWorkerError(
            `failed to write to runner: ${err instanceof Error ? err.message : String(err)}`
          ),
          reject
        );
      }
    });
  }

  /** Route a response frame to the in-flight call (ignores stale post-kill stragglers). */
  deliver(resp: FfmpegResponse): void {
    const call = this.current;
    if (!call || call.id !== resp.id) return;
    this.current = null;
    if (resp.ok) call.resolve(resp.result);
    else call.reject(new FfmpegWorkerError(resp.error.message));
  }

  private fatal(err: FfmpegWorkerError, reject: (e: Error) => void): void {
    this.current = null;
    this.onFatal(err);
    reject(err);
  }
}

/** The ffmpeg driver: spawns the framed-stdio runner and readies a {@link FfmpegConn}. */
class FfmpegDriver implements WorkerDriver<FfmpegConn> {
  readonly name = 'ffmpeg';
  readonly entry: { execPath: string; modulePath: string };
  readonly limits: { timeoutMs: number };

  constructor(execPath: string, runnerPath: string, backstopTimeoutMs: number) {
    this.entry = { execPath, modulePath: runnerPath };
    this.limits = { timeoutMs: backstopTimeoutMs };
  }

  ready(child: ChildHandle, hooks: DriverHooks): Promise<FfmpegConn> {
    return new Promise<FfmpegConn>((resolve, reject) => {
      if (!child.stdout) {
        reject(new FfmpegWorkerError('runner has no stdout'));
        return;
      }
      const decoder = new FrameDecoder();
      let conn: FfmpegConn | null = null;
      child.stdout.on('data', (chunk: Buffer) => {
        let frames: unknown[];
        try {
          frames = decoder.push(chunk);
        } catch (err) {
          const wrapped = new FfmpegWorkerError(
            `runner protocol error: ${err instanceof Error ? err.message : String(err)}`
          );
          if (conn) hooks.onFatal(wrapped);
          else reject(wrapped);
          return;
        }
        for (const msg of frames) {
          const resp = msg as FfmpegResponse;
          if (resp.id === READY_ID) {
            conn = new FfmpegConn(child.stdin, hooks.onFatal);
            resolve(conn);
          } else {
            conn?.deliver(resp);
          }
        }
      });
    });
  }
}

export class FfmpegPool {
  private readonly pool: WorkerPool<FfmpegConn>;
  private readonly defaultTimeoutMs: number;
  private readonly defaultRssBytes: number | null;
  // Outcome counters the pool can't see (the runner reports them as normal results).
  private readonly outcomeKills = { timeout: 0, oom: 0 };
  private rssHighWaterBytes = 0;

  constructor(options: FfmpegPoolOptions) {
    this.defaultTimeoutMs = options.defaultTimeoutMs;
    this.defaultRssBytes = options.rssBytes && options.rssBytes > 0 ? options.rssBytes : null;
    const driver = new FfmpegDriver(
      options.execPath ?? process.execPath,
      resolveRunnerEntry(options.runnerPath),
      options.defaultTimeoutMs + POOL_BACKSTOP_MARGIN_MS
    );
    this.pool = new WorkerPool<FfmpegConn>({
      size: options.size,
      driver,
      spawn: options.spawn,
      ...(options.measureResourcesMs ? { measureResourcesMs: options.measureResourcesMs } : {}),
      logger: options.logger,
    });
  }

  /**
   * Run one ffmpeg/ffprobe to completion. Returns the {@link FfmpegRunResult}
   * (including `outcome: 'exit' | 'timeout' | 'oom'`; a non-zero exit is a normal
   * `'exit'` — the caller decides). Throws {@link FfmpegWorkerError} only for a
   * runner/pool fault (crash, backstop timeout, spawn failure).
   */
  async run(spec: FfmpegRunSpec): Promise<FfmpegRunResult> {
    const timeoutMs = spec.timeoutMs ?? this.defaultTimeoutMs;
    const req: Omit<FfmpegRequest, 'id'> = {
      bin: spec.bin ?? 'ffmpeg',
      args: spec.args,
      timeoutMs,
      rssBytes: spec.rssBytes && spec.rssBytes > 0 ? spec.rssBytes : this.defaultRssBytes,
      stderr: spec.stderr ?? 'tail',
      stdout: spec.stdout ?? false,
    };
    let result: FfmpegRunResult;
    try {
      result = await this.pool.run((conn) => conn.run(req), {
        timeoutMs: timeoutMs + POOL_BACKSTOP_MARGIN_MS,
        label: req.bin,
      });
    } catch (err) {
      // A wedged-runner backstop timeout or a crash — both are runner faults.
      if (err instanceof WorkerPoolTimeoutError || err instanceof WorkerPoolError) {
        throw new FfmpegWorkerError(err.message);
      }
      if (err instanceof FfmpegWorkerError) throw err;
      throw new FfmpegWorkerError(err instanceof Error ? err.message : String(err));
    }
    if (result.outcome === 'timeout') this.outcomeKills.timeout++;
    else if (result.outcome === 'oom') this.outcomeKills.oom++;
    if (result.peakRssBytes && result.peakRssBytes > this.rssHighWaterBytes) {
      this.rssHighWaterBytes = result.peakRssBytes;
    }
    return result;
  }

  stats(): FfmpegPoolStats {
    const s = this.pool.stats();
    return {
      driver: s.driver ?? 'ffmpeg',
      total: s.size,
      idle: s.idle,
      busy: s.busy,
      waiting: s.queued,
      kills: {
        // ffmpeg-child breaches the runner reported (runner stayed warm).
        timeout: this.outcomeKills.timeout,
        oom: this.outcomeKills.oom,
        // Runner faults the pool SIGKILLed. The pool's RSS monitor is off for this
        // driver, so its timeout (backstop on a wedged runner) and oom counters can
        // only be runner faults — fold them into crash so they aren't lost.
        crash: s.kills.crash + s.kills.timeout + s.kills.oom,
      },
      retirements: s.retirements,
      leaseGrace: s.leaseGrace,
      rssHighWaterBytes: this.rssHighWaterBytes,
      saturation: s.saturation,
      ...(s.resourceStats ? { resourceStats: s.resourceStats } : {}),
    };
  }

  /** Toggle the calibration sampler live (see {@link WorkerPool.setMeasureResourcesMs}). */
  setMeasureResourcesMs(ms: number): void {
    this.pool.setMeasureResourcesMs(ms);
  }

  async shutdown(): Promise<void> {
    await this.pool.drain();
  }
}
